import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "../config/axiosConfig";
import {
  Box,
  Avatar,
  Button,
  Fab,
  IconButton,
  TextField,
  Typography,
  Chip,
  Stack,
  Skeleton,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
  CircularProgress,
  ButtonBase,
  useMediaQuery,
} from "@mui/material";
import { alpha, darken, styled, useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/MailOutline";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import BeachAccessIcon from "@mui/icons-material/BeachAccessOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import LockResetIcon from "@mui/icons-material/LockResetOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCameraOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ENV from "../config/env";
import DashboardLayout from "../layouts/DashboardLayout";
import { comprimirImagen } from "../utils/comprimirImagen";
import { SHAPE, type, useM3 } from "../theme/m3";

// Oculta el <input type="file"> sin depender del atributo `hidden`
// (algunos webviews no aplican la regla `[hidden] { display: none }`
// de la hoja de estilos por defecto, y el input nativo queda visible
// flotando fuera del botón).
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  width: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
});

/* ------------------------------------------------------------------ */
/* Helpers de datos                                                    */
/* ------------------------------------------------------------------ */

const DEFAULT_AVATAR = "/uploads/default-avatar.png";
const MAX_AVATAR_MB = 2;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

// `+` tiene más precedencia que `||`: en el original el fallback nunca se usaba.
const buildAvatarUrl = (path, version) => {
  if (!path) return DEFAULT_AVATAR;
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("blob:")) return path;
  const base = `${ENV.API_BASE_URL}${path}`;
  return version ? `${base}${base.includes("?") ? "&" : "?"}v=${version}` : base;
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

/* ------------------------------------------------------------------ */
/* Componentes M3                                                      */
/* ------------------------------------------------------------------ */

/** Encabezado de sección: label pequeño en color primario, como Android Settings. */
const SectionLabel = ({ children }) => {
  const m3 = useM3();
  return (
    <Typography sx={{ ...type.labelMedium, color: m3.primary, px: 2, pt: 3, pb: 1, fontWeight: 700 }}>
      {children}
    </Typography>
  );
};

/**
 * Fila tonal. Cada ítem es su propio bloque redondeado dentro de un grupo,
 * con las esquinas exteriores grandes (estilo Android reciente).
 */
const M3Row = ({ icon, label, value, onClick, position = "middle", tone = "surface", trailing }) => {
  const m3 = useM3();

  const radius = {
    only: SHAPE.xl,
    first: `${SHAPE.xl}px ${SHAPE.xl}px ${SHAPE.xs}px ${SHAPE.xs}px`,
    last: `${SHAPE.xs}px ${SHAPE.xs}px ${SHAPE.xl}px ${SHAPE.xl}px`,
    middle: `${SHAPE.xs}px`,
  }[position];

  const palette =
    tone === "error"
      ? { bg: m3.errorContainer, fg: m3.onErrorContainer, iconBg: alpha(m3.error, 0.16), iconFg: m3.error }
      : tone === "primary"
        ? {
            bg: m3.secondaryContainer,
            fg: m3.onSecondaryContainer,
            iconBg: alpha(m3.primary, 0.18),
            iconFg: m3.primary,
          }
        : {
            bg: m3.surfaceContainer,
            fg: m3.onSurface,
            iconBg: m3.primaryContainer,
            iconFg: m3.onPrimaryContainer,
          };

  const Wrapper = onClick ? ButtonBase : Box;

  return (
    <Wrapper
      {...(onClick ? { onClick, focusRipple: true } : {})}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        textAlign: "left",
        px: 2,
        py: 1.75,
        background: palette.bg,
        color: palette.fg,
        borderRadius: radius,
        transition: "background-color .18s ease, filter .18s ease",
        ...(onClick && {
          cursor: "pointer",
          "&:hover": { filter: "brightness(0.97)" },
        }),
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: SHAPE.full,
          display: "grid",
          placeItems: "center",
          background: palette.iconBg,
          color: palette.iconFg,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...type.bodySmall, color: alpha(palette.fg, 0.75) }}>{label}</Typography>
        <Typography sx={{ ...type.titleMedium, wordBreak: "break-word" }}>{value || "—"}</Typography>
      </Box>

      {trailing ?? (onClick ? <ChevronRightIcon sx={{ opacity: 0.6 }} /> : null)}
    </Wrapper>
  );
};

const RowGroup = ({ children }) => (
  <Stack spacing={0.375}>{children}</Stack>
);

const posOf = (index, total) =>
  total === 1 ? "only" : index === 0 ? "first" : index === total - 1 ? "last" : "middle";

const LoadingState = () => (
  <Stack spacing={2}>
    <Skeleton variant="rounded" height={300} sx={{ borderRadius: `${SHAPE.xl}px` }} />
    <Skeleton variant="rounded" height={72} sx={{ borderRadius: `${SHAPE.xl}px ${SHAPE.xl}px 4px 4px` }} />
    <Skeleton variant="rounded" height={72} sx={{ borderRadius: "4px" }} />
    <Skeleton variant="rounded" height={72} sx={{ borderRadius: `4px 4px ${SHAPE.xl}px ${SHAPE.xl}px` }} />
  </Stack>
);

/* ------------------------------------------------------------------ */
/* Página                                                              */
/* ------------------------------------------------------------------ */

const emptyUser = {
  username: "",
  email: "",
  role: "",
  sucursal: "",
  cargo: "",
  createdAt: "",
  profilePicture: "",
};

const MyAccount = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const m3 = useM3();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { logout } = useAuth();

  const [userData, setUserData] = useState(emptyUser);
  const [diasVacacion, setDiasVacacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const previewRef = useRef(null);
  const fileInputRef = useRef(null);

  const notify = useCallback(
    (message, severity = "success") => setSnackbar({ open: true, message, severity }),
    []
  );

  const authHeader = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  const fetchProfile = useCallback(async () => {
    const headers = authHeader();
    if (!headers) return null;
    const { data } = await axios.get(`${ENV.API_BASE_URL}/api/auth/profile`, { headers });
    setUserData({ ...emptyUser, ...data });
    return data;
  }, [authHeader]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const headers = authHeader();
      if (!headers) {
        setLoading(false);
        setLoadError(true);
        return;
      }

      const [perfil, dias] = await Promise.allSettled([
        axios.get(`${ENV.API_BASE_URL}/api/auth/profile`, { headers }),
        axios.get(`${ENV.API_BASE_URL}/api/permisos/dias-disponibles`, { headers }),
      ]);

      if (cancelled) return;

      if (perfil.status === "fulfilled") setUserData({ ...emptyUser, ...perfil.value.data });
      else {
        console.error("Error obteniendo el perfil del usuario", perfil.reason);
        setLoadError(true);
      }

      if (dias.status === "fulfilled") setDiasVacacion(dias.value.data?.data?.cantidad ?? 0);
      else {
        console.error("Error obteniendo los días de vacación", dias.reason);
        setDiasVacacion(null);
      }

      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authHeader]);

  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    []
  );

  const resetEditForm = useCallback(() => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
    setPreview(null);
    setSelectedFile(null);
    setCurrentPassword("");
    setNewPassword("");
    setShowCurrent(false);
    setShowNew(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const closeEdit = useCallback(() => {
    if (saving) return;
    setEditOpen(false);
    resetEditForm();
  }, [saving, resetEditForm]);

  const handleFileChange = async (event) => {
    const original = event.target.files?.[0];
    event.target.value = "";
    if (!original) return;

    if (!ALLOWED_IMAGE_TYPES.includes(original.type)) {
      notify("Solo se permiten imágenes PNG, JPG o WEBP.", "error");
      return;
    }
    // La foto se achica (máx. 512 px) y se guarda como WebP antes de subirla: un avatar no necesita más y pesa unos pocos KB.
    const { file } = await comprimirImagen(original, { maxLado: 512, calidad: 0.85 });
    if (file.size > MAX_AVATAR_MB * 1024 * 1024) {
      notify(`La imagen no debe superar los ${MAX_AVATAR_MB} MB.`, "error");
      return;
    }

    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setPreview(url);
    setSelectedFile(file);
  };

  const passwordError =
    newPassword && newPassword.length < 8
      ? "Mínimo 8 caracteres"
      : newPassword && newPassword === currentPassword
        ? "Debe ser distinta a la actual"
        : "";

  const hasChanges = Boolean(selectedFile || currentPassword || newPassword);

  const handleGuardarCambios = async () => {
    const cambiaFoto = Boolean(selectedFile);
    const cambiaPassword = Boolean(currentPassword || newPassword);

    if (!cambiaFoto && !cambiaPassword) {
      notify("No hay cambios para guardar.", "info");
      return;
    }

    if (cambiaPassword) {
      if (!currentPassword || !newPassword) {
        notify("Completa ambos campos de contraseña.", "warning");
        return;
      }
      if (passwordError) {
        notify(
          passwordError === "Mínimo 8 caracteres"
            ? "La nueva contraseña debe tener al menos 8 caracteres."
            : "La nueva contraseña no puede ser igual a la actual.",
          "warning"
        );
        return;
      }
    }

    const headers = authHeader();
    if (!headers) {
      notify("Tu sesión expiró. Inicia sesión nuevamente.", "error");
      return;
    }

    setSaving(true);
    try {
      if (cambiaPassword) {
        await axios.put(
          `${ENV.API_BASE_URL}/api/auth/update-password`,
          { currentPassword, newPassword },
          { headers }
        );
      }

      if (cambiaFoto) {
        const formData = new FormData();
        formData.append("profilePicture", selectedFile);
        await axios.post(`${ENV.API_BASE_URL}/api/auth/upload-profile`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        await fetchProfile().catch(() => {});
        setAvatarVersion(Date.now());
      }

      notify("Cambios guardados correctamente.");
      setEditOpen(false);
      resetEditForm();
    } catch (error) {
      const apiMessage = error?.response?.data?.error || error?.response?.data?.message;
      if (error?.response?.status === 401) {
        notify(apiMessage || "La contraseña actual no es correcta.", "error");
      } else {
        if (!apiMessage) console.error("Error guardando cambios de cuenta", error);
        notify(apiMessage || "Error al guardar los cambios.", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const avatarSrc = buildAvatarUrl(userData.profilePicture, avatarVersion);

  /* --- estilos reutilizables M3 --- */
  const filledButton = {
    ...type.labelLarge,
    textTransform: "none",
    borderRadius: SHAPE.full,
    px: 3,
    py: 1.25,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  };

  const tonalButton = {
    ...filledButton,
    background: m3.secondaryContainer,
    color: m3.onSecondaryContainer,
    "&:hover": { background: darken(m3.secondaryContainer, 0.06), boxShadow: "none" },
  };

  const textFieldSx = {
    "& .MuiFilledInput-root": {
      borderRadius: `${SHAPE.xs}px ${SHAPE.xs}px 0 0`,
      background: m3.surfaceHigh,
      "&:hover": { background: m3.surfaceHigh, filter: "brightness(0.98)" },
      "&.Mui-focused": { background: m3.surfaceHigh },
    },
  };

  const infoRows = [
    { icon: <PersonIcon />, label: "Nombre de usuario", value: userData.username },
    { icon: <EmailIcon />, label: "Correo electrónico", value: userData.email },
    { icon: <AdminPanelSettingsIcon />, label: "Rol", value: userData.role },
  ];

  const orgRows = [
    { icon: <BusinessIcon />, label: "Sucursal", value: userData.sucursal },
    { icon: <BadgeIcon />, label: "Cargo", value: userData.cargo },
  ];

  return (
    <DashboardLayout>
      <Box
        sx={{
          minHeight: "100%",
          background: m3.surfaceLow,
          pb: 12,
        }}
      >
        <Box sx={{ maxWidth: 1040, mx: "auto", px: 2 }}>
          {/* --- Large top app bar (M3) --- */}
          <Box sx={{ pt: { xs: 3, md: 5 }, pb: 2 }}>
            <Typography sx={{ ...type.displaySmall, color: m3.onSurface }}>Mi cuenta</Typography>
          </Box>

          {loading ? (
            <LoadingState />
          ) : (
            <>
              {loadError && (
                <Alert
                  severity="warning"
                  sx={{ borderRadius: `${SHAPE.lg}px`, mb: 2, ...type.bodyMedium }}
                >
                  No pudimos cargar toda tu información. Intenta recargar la página.
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "320px 1fr", lg: "300px 1fr 280px" },
                  alignItems: "start",
                  gap: 3,
                }}
              >
                {/* ================= Columna 1: perfil ================= */}
                <Box
                  sx={{
                    gridColumn: "1",
                    gridRow: { xs: "1", md: "1", lg: "1" },
                    background: m3.primaryContainer,
                    color: m3.onPrimaryContainer,
                    borderRadius: `${SHAPE.xl}px`,
                    px: 3,
                    py: 4,
                    textAlign: "center",
                  }}
                >
                    <Box sx={{ position: "relative", width: 120, mx: "auto" }}>
                      <Avatar
                        alt={userData.username || "Usuario"}
                        src={avatarSrc}
                        sx={{
                          width: 120,
                          height: 120,
                          fontSize: 44,
                          fontWeight: 500,
                          bgcolor: m3.primary,
                          color: m3.onPrimary,
                        }}
                      >
                        {getInitials(userData.username)}
                      </Avatar>
                      <Tooltip title="Cambiar foto">
                        <IconButton
                          onClick={() => setEditOpen(true)}
                          aria-label="Cambiar foto de perfil"
                          sx={{
                            position: "absolute",
                            right: -4,
                            bottom: -4,
                            width: 40,
                            height: 40,
                            borderRadius: `${SHAPE.md}px`,
                            background: m3.surfaceHigh,
                            color: m3.primary,
                            "&:hover": { background: m3.surfaceHigh, filter: "brightness(0.95)" },
                          }}
                        >
                          <PhotoCameraIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Typography sx={{ ...type.headlineSmall, mt: 2.5, fontWeight: 500 }}>
                      {userData.username || "—"}
                    </Typography>
                    <Typography sx={{ ...type.bodyMedium, opacity: 0.8, mt: 0.5 }}>
                      {userData.email || "—"}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      justifyContent="center"
                      sx={{ mt: 2 }}
                    >
                      {userData.cargo && (
                        <Chip
                          label={userData.cargo}
                          sx={{
                            ...type.labelLarge,
                            borderRadius: SHAPE.sm,
                            background: alpha(m3.primary, 0.16),
                            color: m3.onPrimaryContainer,
                          }}
                        />
                      )}
                      {userData.role && (
                        <Chip
                          label={userData.role}
                          variant="outlined"
                          sx={{
                            ...type.labelLarge,
                            borderRadius: SHAPE.sm,
                            borderColor: alpha(m3.onPrimaryContainer, 0.3),
                            color: m3.onPrimaryContainer,
                          }}
                        />
                      )}
                    </Stack>

                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setEditOpen(true)}
                      sx={{
                        ...filledButton,
                        mt: 3,
                        background: m3.primary,
                        color: m3.onPrimary,
                        display: { xs: "none", sm: "inline-flex" },
                        "&:hover": { background: darken(m3.primary, 0.08), boxShadow: "none" },
                      }}
                    >
                      Editar perfil
                    </Button>
                </Box>

                {/* ================= Columna 2: secciones ================= */}
                <Stack
                  spacing={0}
                  sx={{
                    gridColumn: { xs: "1", md: "2", lg: "2" },
                    gridRow: { xs: "2", md: "1", lg: "1" },
                  }}
                >
                  {/* ---------- Datos personales ---------- */}
                  <SectionLabel>Información personal</SectionLabel>
                  <RowGroup>
                    {infoRows.map((r, i) => (
                      <M3Row key={r.label} {...r} position={posOf(i, infoRows.length)} />
                    ))}
                  </RowGroup>

                  {/* ---------- Datos laborales ---------- */}
                  <SectionLabel>Datos laborales</SectionLabel>
                  <RowGroup>
                    {orgRows.map((r, i) => (
                      <M3Row key={r.label} {...r} position={posOf(i, orgRows.length)} />
                    ))}
                  </RowGroup>

                  {/* ---------- Cuenta ---------- */}
                  <SectionLabel>Cuenta y seguridad</SectionLabel>
                  <RowGroup>
                    <M3Row
                      icon={<LockResetIcon />}
                      label="Contraseña"
                      value="Cambiar contraseña"
                      position="first"
                      onClick={() => setEditOpen(true)}
                    />
                    <M3Row
                      icon={<LogoutIcon />}
                      label="Sesión"
                      value="Cerrar sesión"
                      tone="error"
                      position="last"
                      onClick={() => setLogoutConfirmOpen(true)}
                    />
                  </RowGroup>
                </Stack>

                {/* ================= Columna 3: vacaciones (verde) ================= */}
                <Box
                  sx={{
                    gridColumn: { xs: "1", md: "1", lg: "3" },
                    gridRow: { xs: "3", md: "2", lg: "1" },
                  }}
                >
                  <SectionLabel>Vacaciones</SectionLabel>
                  <Box
                    sx={{
                      background: m3.successContainer,
                      color: m3.onSuccessContainer,
                      borderRadius: `${SHAPE.xl}px`,
                      p: 3,
                      display: "flex",
                      flexDirection: { xs: "row", lg: "column" },
                      alignItems: { xs: "center", lg: "stretch" },
                      textAlign: { xs: "left", lg: "center" },
                      gap: 2.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: SHAPE.full,
                        display: "grid",
                        placeItems: "center",
                        background: alpha(m3.success, 0.18),
                        color: m3.success,
                        flexShrink: 0,
                        mx: { xs: 0, lg: "auto" },
                      }}
                    >
                      <BeachAccessIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 140 }}>
                      <Typography sx={{ ...type.bodyMedium, opacity: 0.8 }}>Días disponibles</Typography>
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={1}
                        justifyContent={{ xs: "flex-start", lg: "center" }}
                      >
                        <Typography sx={{ ...type.displaySmall, fontWeight: 500 }}>
                          {diasVacacion === null ? "—" : diasVacacion}
                        </Typography>
                        <Typography sx={{ ...type.bodyMedium, opacity: 0.8 }}>
                          {diasVacacion === 1 ? "día" : "días"}
                        </Typography>
                      </Stack>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate("/permisos/mis-solicitudes")}
                      sx={{
                        ...filledButton,
                        background: m3.success,
                        color: m3.onSuccess,
                        "&:hover": { background: darken(m3.success, 0.08), boxShadow: "none" },
                      }}
                    >
                      Mis solicitudes
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* --- FAB extendido (solo móvil, como en Android) --- */}
        {!loading && (
          <Fab
            variant="extended"
            onClick={() => setEditOpen(true)}
            sx={{
              position: "fixed",
              right: 16,
              bottom: 16,
              display: { xs: "inline-flex", sm: "none" },
              textTransform: "none",
              ...type.labelLarge,
              height: 56,
              px: 2.5,
              borderRadius: `${SHAPE.lg}px`,
              background: m3.primaryContainer,
              color: m3.onPrimaryContainer,
              boxShadow: theme.shadows[3],
              "&:hover": { background: darken(m3.primaryContainer, 0.05) },
            }}
          >
            <EditIcon sx={{ mr: 1 }} />
            Editar
          </Fab>
        )}
      </Box>

      {/* ============ Diálogo de edición ============ */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              borderRadius: isMobile ? 0 : `${SHAPE.xl}px`,
              background: m3.surfaceHigh,
              backgroundImage: "none",
            },
          },
        }}
      >
        {/* Top app bar del full-screen dialog (patrón Android) */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, pt: 2, pb: 1 }}>
          <IconButton onClick={closeEdit} aria-label="Cerrar" disabled={saving}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ...type.titleLarge, flex: 1, fontWeight: 500 }}>Editar cuenta</Typography>
          {isMobile && (
            <Button
              onClick={handleGuardarCambios}
              disabled={saving || !hasChanges || Boolean(passwordError)}
              sx={{ ...type.labelLarge, textTransform: "none", borderRadius: SHAPE.full, color: m3.primary }}
            >
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          )}
        </Stack>

        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ ...type.labelMedium, color: m3.primary, fontWeight: 700, mb: 1.5 }}>
            FOTO DE PERFIL
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              background: m3.surfaceContainer,
              borderRadius: `${SHAPE.lg}px`,
              p: 2,
              mb: 3,
            }}
          >
            <Avatar
              alt={userData.username || "Usuario"}
              src={preview || avatarSrc}
              sx={{ width: 64, height: 64, bgcolor: m3.primary, color: m3.onPrimary, fontWeight: 500 }}
            >
              {getInitials(userData.username)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Button
                component="label"
                startIcon={<PhotoCameraIcon />}
                sx={{ ...tonalButton, py: 0.75 }}
              >
                Elegir foto
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_IMAGE_TYPES.join(",")}
                  onChange={handleFileChange}
                />
              </Button>
              <Typography sx={{ ...type.bodySmall, color: m3.onSurfaceVariant, mt: 0.75 }} noWrap>
                {selectedFile ? selectedFile.name : `PNG, JPG o WEBP · máx. ${MAX_AVATAR_MB} MB`}
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ ...type.labelMedium, color: m3.primary, fontWeight: 700, mb: 1.5 }}>
            CAMBIAR CONTRASEÑA
          </Typography>
          {/* Form propio: le da a Chrome un límite claro de "formulario",
              así no asocia estos campos con inputs de otras partes de la
              página (p. ej. el buscador del menú) al autocompletar. */}
          <Box component="form" onSubmit={(e) => e.preventDefault()} autoComplete="off">
          <Stack spacing={2}>
            <TextField
              fullWidth
              variant="filled"
              label="Contraseña actual"
              type={showCurrent ? "text" : "password"}
              // "new-password" (en vez de "current-password") evita que el
              // navegador autocomplete este campo con la contraseña guardada
              // del sitio cada vez que se abre el diálogo.
              autoComplete="new-password"
              name="account-current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={textFieldSx}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowCurrent((v) => !v)}
                        aria-label={showCurrent ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              fullWidth
              variant="filled"
              label="Nueva contraseña"
              type={showNew ? "text" : "password"}
              autoComplete="new-password"
              name="account-new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={Boolean(passwordError)}
              helperText={passwordError || "Mínimo 8 caracteres"}
              sx={textFieldSx}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowNew((v) => !v)}
                        aria-label={showNew ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
          </Box>
          <Typography sx={{ ...type.bodySmall, color: m3.onSurfaceVariant, mt: 2 }}>
            Puedes cambiar solo la foto, solo la contraseña, o ambas.
          </Typography>
        </DialogContent>

        {!isMobile && (
          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button
              onClick={closeEdit}
              disabled={saving}
              sx={{ ...type.labelLarge, textTransform: "none", borderRadius: SHAPE.full, color: m3.primary }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleGuardarCambios}
              disabled={saving || !hasChanges || Boolean(passwordError)}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                ...filledButton,
                background: m3.primary,
                color: m3.onPrimary,
                "&:hover": { background: darken(m3.primary, 0.08), boxShadow: "none" },
              }}
            >
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ============ Confirmación de cierre de sesión ============ */}
      <Dialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: `${SHAPE.xl}px`,
              background: m3.surfaceHigh,
              backgroundImage: "none",
              p: 1,
            },
          },
        }}
      >
        <Stack alignItems="center" sx={{ pt: 3 }}>
          <LogoutIcon sx={{ color: m3.primary, fontSize: 26 }} />
          <Typography sx={{ ...type.headlineSmall, mt: 2, fontWeight: 400 }}>Cerrar sesión</Typography>
        </Stack>
        <DialogContent>
          <Typography sx={{ ...type.bodyMedium, color: m3.onSurfaceVariant, textAlign: "center" }}>
            Vas a tener que ingresar tus credenciales de nuevo para volver a entrar.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLogoutConfirmOpen(false)}
            sx={{ ...type.labelLarge, textTransform: "none", borderRadius: SHAPE.full, color: m3.primary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleLogout}
            sx={{ ...type.labelLarge, textTransform: "none", borderRadius: SHAPE.full, color: m3.error }}
          >
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ Snackbar M3 ============ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: isMobile ? "center" : "left" }}
        sx={{ mb: { xs: 10, sm: 2 } }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{
            ...type.bodyMedium,
            borderRadius: `${SHAPE.xs}px`,
            background: m3.inverseSurface,
            color: m3.inverseOnSurface,
            boxShadow: theme.shadows[6],
            "& .MuiAlert-icon": {
              color:
                snackbar.severity === "error"
                  ? theme.palette.error.light
                  : snackbar.severity === "warning"
                    ? theme.palette.warning.light
                    : theme.palette.success.light,
            },
            "& .MuiAlert-action .MuiIconButton-root": { color: m3.inverseOnSurface },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default MyAccount;